import React, { memo, useMemo } from "react";
import {
    Option,
    InfoWrapper,
    TextDivider,
    Flex,
    Sla,
    TextNoWrap,
    IconWrapper,
    DeliveryOptionHeaderContainer,
} from "./styles";
import { getDeliveryOptionA11yLabel, getOptionStyles } from "./helpers";
import { DeliveryOptionProps } from "./types";
import DeliverySpeedPointContainer from "@cart-ui/components/delivery-speed/components/delivery-speed-point-container";
import DeliverySpeedOfferInfo from "@cart-ui/components/delivery-speed/components/deliver-speed-offer-info";
import {
    getDeliverySpeedItemDataTestId,
    getDeliverySpeedItemSubheadingTestId,
    getSlaText,
    isLastOptionWithOneSla,
    getDeliverySpeedItemIconDataTestId,
} from "@cart-ui/helpers/DeliverySpeed";
import { DeliverySpeedDetail } from "@swiggy-private/cart-types";
import { ExpressIcon } from "./icons";
import Banner from "./Banner";
import { isEmpty } from "@swiggy-private/js-utils";
import { replaceText } from "@swiggy-private/core-ui";

function DeliveryOption({
    selected,
    heading,
    headingInfo,
    subHeading,
    isDisabled,
    meta,
    slaData,
    option,
    isLastOption,
    isFirstOption,
    bannerData,
    onClick,
    setShowM2ChalfcardOnClick,
    impressionExpressBuyBlckNudge,
    clickExpressBuyBlckNudge,
    offerInfo,
    onOfferTimerEnds,
    impressionDeliveryTypeOffer,
    impressionDeliveryTypeOfferExpired,
    deliverySpeedWithIcon,
}: DeliveryOptionProps) {
    const { showShimmer = false, tooltipData = {}, highlightColor, headingInfoColor } = meta || {};

    const { headingVariant, headingColor, slaColor, slaVariant, subHeadingVariant, subHeadingColor } = useMemo(
        () => getOptionStyles(selected, isDisabled, highlightColor),
        [selected, isDisabled, highlightColor],
    );

    const slaText = useMemo(() => getSlaText(slaData), [slaData]);

    const ariaLabel = useMemo(
        () =>
            getDeliveryOptionA11yLabel({
                option: heading,
                selected,
                isDisabled,
                sla: slaText,
                savings: headingInfo,
                disabledText: subHeading,
            }),
        [heading, selected, isDisabled, slaText, headingInfo, subHeading],
    );

    const deliverySpeedItemDataTestId = useMemo(() => {
        return getDeliverySpeedItemDataTestId(slaData as DeliverySpeedDetail, Boolean(selected));
    }, [selected, slaData]);

    const deliverySpeedItemSubheadingTestId = useMemo(() => {
        return getDeliverySpeedItemSubheadingTestId({
            type: slaData?.type,
            heading,
            subHeading,
            isDisabled,
            isLastOption,
        });
    }, [heading, isDisabled, isLastOption, slaData?.type, subHeading]);

    const deliveryIconAsHeading = useMemo(() => {
        return !isEmpty(deliverySpeedWithIcon) ? replaceText(deliverySpeedWithIcon, "", true) : null;
    }, [deliverySpeedWithIcon]);

    const deliverySpeedItemIconDataTestId = useMemo(() => {
        return getDeliverySpeedItemIconDataTestId(slaData as DeliverySpeedDetail);
    }, [slaData]);

    return (
        <>
            <Option
                tabIndex={0}
                role="button"
                aria-label={ariaLabel}
                isLastOption={isLastOption}
                isFirstOption={isFirstOption}
                isDisabled={isDisabled}
                onClick={onClick}
                data-testid={deliverySpeedItemDataTestId}
            >
                <InfoWrapper
                    aria-hidden="true"
                    topMarginRequired={isLastOptionWithOneSla({ heading, subHeading, isDisabled, isLastOption })}
                >
                    <DeliveryOptionHeaderContainer>
                        {!isEmpty(deliveryIconAsHeading) ? (
                            <IconWrapper data-testid={deliverySpeedItemIconDataTestId}>
                                {deliveryIconAsHeading}
                            </IconWrapper>
                        ) : (
                            <Flex>
                                <TextNoWrap variant={headingVariant} color={headingColor}>
                                    {heading}
                                </TextNoWrap>
                                <ExpressIcon
                                    type={option}
                                    selected={selected}
                                    disabled={isDisabled}
                                    highlightColor={highlightColor}
                                />
                                {headingInfo && (
                                    <Flex>
                                        <TextDivider
                                            color={isDisabled ? "text_disabled" : "text_Low_Emphasis"}
                                            variant="V2.body3.neutral.regular"
                                        >
                                            |
                                        </TextDivider>

                                        <DeliverySpeedPointContainer
                                            text={headingInfo}
                                            shimmer={showShimmer}
                                            tooltipData={tooltipData}
                                            highlightColor={headingInfoColor}
                                        />
                                    </Flex>
                                )}
                            </Flex>
                        )}
                        {slaText && (
                            <Sla aria-hidden="true" color={slaColor} variant={slaVariant}>
                                {slaText}
                            </Sla>
                        )}
                    </DeliveryOptionHeaderContainer>
                    {subHeading && (
                        <TextNoWrap
                            variant={subHeadingVariant}
                            color={subHeadingColor}
                            data-testid={deliverySpeedItemSubheadingTestId}
                        >
                            {subHeading}
                        </TextNoWrap>
                    )}
                </InfoWrapper>

                {!isEmpty(offerInfo) && !isEmpty(offerInfo?.offerMessage) && (
                    <DeliverySpeedOfferInfo
                        offerInfo={offerInfo}
                        onTimeEnd={onOfferTimerEnds}
                        impressionDeliveryTypeOffer={impressionDeliveryTypeOffer}
                        impressionDeliveryTypeOfferExpired={impressionDeliveryTypeOfferExpired}
                    />
                )}
                <Banner
                    bannerData={bannerData}
                    slaData={slaData}
                    setShowM2ChalfcardOnClick={setShowM2ChalfcardOnClick}
                    impressionExpressBuyBlckNudge={impressionExpressBuyBlckNudge}
                    clickExpressBuyBlckNudge={clickExpressBuyBlckNudge}
                    headingInfo={headingInfo}
                />
            </Option>
        </>
    );
}

export default memo(DeliveryOption);
